import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useThemeManager } from '@/hooks/useTheme';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { fetchPopularMovies, fetchTrendingMovies, fetchHackerNewsStories, fetchTrendingRepos } from '@/store/slices/contentSlice';
import { 
  BackIcon, 
  ThemeToggleIcon,
  RefreshIcon
} from '@/components/icons';

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px; /* iOS标准导航栏高度 */
  padding: 0 ${({ theme }) => theme.spacing.md};
  background: transparent;
  position: relative;
`;

const NavTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.headline};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  text-align: center;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  
  /* iOS原生文本样式 */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  letter-spacing: -0.01em;
  line-height: 1.2;
  
  /* 文本溢出处理 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
`;

const NavButton = styled(motion.button)`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-height: 36px;
  min-width: 36px;
  margin: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* iOS原生触摸优化 */
  -webkit-tap-highlight-color: transparent;
  tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  
  /* 确保Fluent Icons大小一致 */
  svg {
    width: 20px !important;
    height: 20px !important;
    font-size: 20px !important;
  }
  
  /* 只在非触摸设备上启用hover效果 */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ theme }) => theme.colors.fill.tertiary};
    }
  }
  
  &:active {
    background: ${({ theme }) => theme.colors.fill.secondary};
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  min-width: 60px;
  justify-content: flex-start;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  min-width: 60px;
  justify-content: flex-end;
`;

const BackButton = styled(NavButton)`
  font-size: 16px;
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface TopNavigationProps {
  title: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  rightActions?: React.ReactNode;
  onRefresh?: () => void; // 可选：页面自定义刷新
}

const TopNavigation: React.FC<TopNavigationProps> = ({
  title,
  showBackButton = false,
  onBackClick,
  rightActions,
  onRefresh
}) => {
  const { theme, toggleTheme } = useThemeManager();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { home, discover, favorites } = useAppSelector((state) => state.content);

  const isRefreshing = (() => {
    if (location.pathname === '/discover') {
      return discover.popularLoading || discover.trendingLoading;
    }
    if (location.pathname === '/favorites') {
      return favorites.loading;
    }
    if (location.pathname === '/' || location.pathname === '') {
      return home.loading;
    }
    return false;
  })();

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
      return;
    }
    // 根据当前路由触发对应页面的数据刷新
    if (location.pathname === '/discover') {
      dispatch(fetchPopularMovies());
      dispatch(fetchTrendingMovies());
      return;
    }
    if (location.pathname === '/favorites') {
      dispatch(fetchHackerNewsStories());
      return;
    }
    if (location.pathname === '/' || location.pathname === '') {
      dispatch(fetchTrendingRepos());
      return;
    }
    // 其他页面：留空，必要时可在各页面传入 onRefresh 覆盖
  };

  return (
    <NavContainer>
      <LeftSection>
        <NavButton
          onClick={handleRefresh}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="刷新"
          title="刷新当前页面数据"
          animate={isRefreshing ? { rotate: 360 } : { rotate: 0 }}
          transition={isRefreshing ? { repeat: Infinity, duration: 0.8, ease: 'linear' } : { duration: 0.2 }}
        >
          <RefreshIcon size={20} />
        </NavButton>
        {showBackButton && (
          <BackButton
            onClick={onBackClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="返回"
          >
            <BackIcon size={20} />
          </BackButton>
        )}
      </LeftSection>
      
      <NavTitle>{title}</NavTitle>
      
      <RightSection>
        {rightActions}
        
        {/* 主题切换按钮 */}
        <NavButton
          onClick={toggleTheme}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title={`切换到${theme === 'light' ? '暗色' : '亮色'}主题`}
          aria-label={`切换到${theme === 'light' ? '暗色' : '亮色'}主题`}
        >
          <ThemeToggleIcon isDark={theme === 'dark'} size={20} />
        </NavButton>
      </RightSection>
    </NavContainer>
  );
};

export default TopNavigation;